import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { HiChevronDown, HiCheck, HiOutlineUser, HiOutlineMail, HiOutlinePhone, HiOutlineLocationMarker, HiOutlineOfficeBuilding, HiOutlineMailOpen } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchSettings } from '../../../admin/settings/controller/settings-controller';
import { updateOrderProduct, createOrder, fetchOrderNewReference } from '../../../admin/orders-controller/order-controller';
import { promoCodeValidationService } from '../../../../services/promoCodeValidationService'; // Add this import
import facebookConversionService from '../../../../services/facebookConversionService';
import { sendOrderConfirmationEmail } from '../../../../services/orderTrackingService';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CustomDropdown from './custom-dropdown';
import { useProductInfoStyles } from '../templates/useProductInfoStyles';

// Tunisian cities with codes
const tunisianCities = [
  { value: "TUN", label: "Tunis" },
  { value: "SFX", label: "Sfax" },
  { value: "SOS", label: "Sousse" },
  { value: "KRN", label: "Kairouan" },
  { value: "BIZ", label: "Bizerte" },
  { value: "GBS", label: "Gabes" },
  { value: "AM", label: "Ariana" },
  { value: "GFS", label: "Gafsa" },
  { value: "MNS", label: "Monastir" },
  { value: "BA", label: "Ben Arous" },
  { value: "KSR", label: "Kasserine" },
  { value: "MDN", label: "Medenine" },
  { value: "NAB", label: "Nabeul" },
  { value: "TAN", label: "Tataouine" },
  { value: "BJA", label: "Beja" },
  { value: "JDB", label: "Jendouba" },
  { value: "KEF", label: "Kef" },
  { value: "MHD", label: "Mahdia" },
  { value: "SBZ", label: "Sidi Bouzid" },
  { value: "TZR", label: "Tozeur" },
  { value: "SLI", label: "Siliana" },
  { value: "KBL", label: "Kebili" },
  { value: "ZAG", label: "Zaghouan" },
  { value: "MAN", label: "Manouba" },
];

// Generic, type-agnostic ascending sort for variant values.
// Works for numbers ("40, 41, 46"), words ("Noir, Marron"), mixed labels
// ("XS, S, M"), or anything the merchant configures. Uses localeCompare with
// numeric collation so "9" comes before "10", and is case-insensitive.
const sortByLabelAsc = (arr, getLabel = (x) => x) =>
  [...arr].sort((a, b) =>
    String(getLabel(a) ?? '').localeCompare(
      String(getLabel(b) ?? ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    )
  );

const ProductInfo = ({
  product,
  selectedVariant,
  quantity,
  onQuantityChange,
  onVariantChange,
  onAddToCart,
  onImageChange,
  variantTypes,
  selectedPack,
  packVariantSelections,
  onPackSelect,
  onPackVariantSelect
}) => {
  const tStyles = useProductInfoStyles();
  // State for checkout form
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    codePromo: '',
    notes: '',
    paymentMethod: 'cash',
    terms: true
  });

  // NEW - Add promo code state
  const [promoCodeState, setPromoCodeState] = useState({
    isValidating: false,
    isValid: false,
    discount: 0,
    validatedCode: null,
    error: null
  });

  const [errors, setErrors] = useState({});
  const [formComplete, setFormComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddedToCart, setShowAddedToCart] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [orderReference, setOrderReference] = useState('');
  const [orderId, setOrderId] = useState(null);
  const navigate = useNavigate();

  // Refs for abandoned order tracking
  const phoneInputRef = useRef(null);
  const abandonedOrderCreatedRef = useRef(false);
  const referenceRef = useRef(null);
  const successfulSubmissionRef = useRef(false); // New ref for tracking successful submissions

  // Refs for form fields (scroll-to-error)
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const addressRef = useRef(null);
  const cityRef = useRef(null);
  const formRef = useRef(null);
  const submitButtonRef = useRef(null);
  const [showFixedButton, setShowFixedButton] = useState(true);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const { platformid } = useParams();
  const location = useLocation();

  // Prefill customer info when coming from order confirmation suggestions
  useEffect(() => {
    const prefill = location.state?.prefillCustomer;
    if (prefill) {
      setFormData(prev => ({
        ...prev,
        firstName: prefill.firstName || prev.firstName,
        lastName: prefill.lastName || prev.lastName,
        email: prefill.email || prev.email,
        phone: prefill.phone || prev.phone,
        address: prefill.address || prev.address,
        city: prefill.city || prev.city
      }));
    }
  }, [location.state]);

  // Hide fixed mobile button when original submit buttons are visible
  useEffect(() => {
    if (!submitButtonRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowFixedButton(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(submitButtonRef.current);
    return () => observer.disconnect();
  }, []);
  // Extract variant names from product variants
  const variants = useMemo(() => {
    if (!product || !product.productVariants || product.productVariants.length === 0) {
      return [];
    }

    // Return array of variant objects with id and name
    return product.productVariants.map(variant => ({
      id: variant.id,
      name: variant.name,
      reference: variant.reference,
      stock: variant.stock || 0,
      displayName: variant.name // Use full name directly
    }));
  }, [product]);

  // Fetch settings including delivery fee on component mount
  useEffect(() => {
    const getSettings = async () => {
      try {
        const settings = await fetchSettings();
        if (settings && settings.fraisDeLivraison !== undefined) {
          setDeliveryFee(settings.fraisDeLivraison);
        }
      } catch (error) {
        console.error('Error fetching delivery fee:', error);
      }
    };

    getSettings();
  }, []);

  // Track Facebook ViewContent so visitors from Meta ads are counted on this product
  useEffect(() => {
    if (!product?.id || !product.categorie?.pixelId) return;
    facebookConversionService.trackViewContent(
      {
        productId: product.id,
        productName: product.name,
        value: product.prixVenteTTC || 0,
        currency: 'TND',
        contentType: 'product',
      },
      product.categorie
    );
  }, [product?.id]);

  // Defer order reference fetch — only load when user starts interacting with the form
  const orderRefFetched = useRef(false);
  const _fetchOrderRefPromise = useRef(null);
  const fetchOrderRef = useCallback(() => {
    if (orderRefFetched.current || orderReference) return _fetchOrderRefPromise.current || Promise.resolve();
    if (_fetchOrderRefPromise.current) return _fetchOrderRefPromise.current;
    orderRefFetched.current = true;
    _fetchOrderRefPromise.current = (async () => {
      try {
        const reference = await fetchOrderNewReference();
        setOrderReference(reference);
        referenceRef.current = reference && typeof reference === 'object' && reference.numero
          ? reference.numero
          : 'CMD-000001';
      } catch (error) {
        console.error('Error fetching order reference:', error);
        setOrderReference('CMD-000001');
        referenceRef.current = 'CMD-000001';
      }
    })();
    return _fetchOrderRefPromise.current;
  }, [orderReference]);

  // Fetch reference when user starts typing in any form field
  const handleFormInteraction = useCallback(() => {
    fetchOrderRef();
  }, [fetchOrderRef]);

  // Get label for stock display
  const getStockLabel = () => {
  // For products without variants, use product.stock directly
  if (!product.isVariant || !product.productVariants || product.productVariants.length === 0) {
    if (!product.stock || product.stock === 0) return 'Rupture de stock';
    if (product.stock <= 5) return `Stock limité (${product.stock})`;
    return `En stock (${product.stock})`;
  }
  
  // For products with variants, use selectedVariant.stock
  if (!selectedVariant) return 'Indisponible';
  if (selectedVariant.stock === 0) return 'Rupture de stock';
  if (selectedVariant.stock <= 5) return `⚠️ Plus que ${selectedVariant.stock} en stock !`;
  return `En stock (${selectedVariant.stock})`;
};

  // Set color for stock status
  const stockLabelColor = () => {
  // For products without variants, use product.stock directly
  if (!product.isVariant || !product.productVariants || product.productVariants.length === 0) {
    if (!product.stock || product.stock === 0) return 'text-red-600';
    if (product.stock <= 5) return 'text-amber-600';
    return 'text-blue-600';
  }
  
  // For products with variants, use selectedVariant.stock
  if (!selectedVariant || selectedVariant.stock === 0) return 'text-red-600';
  if (selectedVariant.stock <= 5) return 'text-amber-600';
  return 'text-blue-600';
};

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
  };

  const cartNotification = {
    hidden: { opacity: 0, scale: 0.8, y: -10 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 15
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2
      }
    }
  };

  // Calculate discount if needed (based on promo)
  const hasDiscount = product?.enPromo && product?.prixVentePromo > 0 && product?.prixVentePromo < product?.prixVenteTTC;
  const discountPercentage = hasDiscount
    ? Math.round(((product?.prixVenteTTC - product?.prixVentePromo) / product?.prixVenteTTC) * 100)
    : 0;

  // Calculate if this product qualifies for free delivery
  const displayPrice = selectedPack ? selectedPack.prixPack : (hasDiscount ? product?.prixVentePromo : product?.prixVenteTTC);
  const productTotal = (displayPrice || 0) * quantity;
  const isFreeDelivery = product?.livraisonGratuite && quantity >= (product?.seuilLivraisonGratuite || 1);
  const finalDeliveryFee = isFreeDelivery ? 0 : deliveryFee;
  const promoDiscount = promoCodeState.isValid ? promoCodeState.discount : 0;

  // Apply promo discount to TTC and recalculate HT and TVA
  const discountedTTC = Math.max(0, productTotal - promoDiscount);
  const finalHT = discountedTTC / 1.19; // Recalculate HT from discounted TTC
  const finalTVA = discountedTTC - finalHT; // Recalculate TVA from discounted TTC
  const grandTotal = discountedTTC + finalDeliveryFee;

  // Handle clicking a variant
  const handleVariantSelect = (variantId) => {
    onVariantChange(variantId);

    // Update the main image if this variant has an image
    const variant = product.productVariants.find(v => v.id === variantId);
    if (variant && variant.imageUrl && variant.imageUrl.length > 0 && variant.imageUrl[0]) {
      onImageChange(variant.imageUrl[0]);
    }
  };

  // Phone input change handler with abandoned order creation
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Lazily fetch order reference on first form interaction
    handleFormInteraction();
    
    // Create a special case for phone field
    if (name === 'phone') {
      const phoneValue = value.replace(/\D/g, '').slice(0, 8);
      
      // Update form data with the new phone number
      setFormData(prev => ({
        ...prev,
        phone: phoneValue
      }));
      
      // Clear validation error when user types
      if (errors.phone) {
        setErrors(prev => ({ ...prev, phone: null }));
      }
      
      // Create abandoned order when phone number reaches exactly 8 digits for the first time
      if (phoneValue.length === 8 && !abandonedOrderCreatedRef.current) {
        // Ensure reference is loaded before creating abandoned order
        fetchOrderRef().then(() => {
          if (referenceRef.current && !abandonedOrderCreatedRef.current) {
            createAbandonedOrder(phoneValue);
          }
        });
      }
    } else if (name === 'codePromo') {
      // Handle promo code changes
      setFormData(prev => ({
        ...prev,
        codePromo: value
      }));
      
      // Reset promo code state when user types
      setPromoCodeState({
        isValidating: false,
        isValid: false,
        discount: 0,
        validatedCode: null,
        error: null
      });
      
      // Clear validation error when user types
      if (errors.codePromo) {
        setErrors(prev => ({ ...prev, codePromo: null }));
      }
    } else {
      // Handle other form fields normally
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));

      // Persist identity fields to Facebook Advanced Matching as soon as they
      // are entered so every subsequent event (PageView, ViewContent, AddToCart…)
      // carries hashed fn/ln/ct/em. This raises the customer-data match coverage
      // well above the ~23% obtained when these were only sent at Purchase time.
      if (['firstName', 'lastName', 'city', 'email'].includes(name) && value) {
        try {
          facebookConversionService.setCustomer({ [name]: value, country: 'TN' });
        } catch { /* ignore */ }
      }

      // Clear validation error when user types
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: null }));
      }
    }
  };

  // Create a separate function to handle abandoned order creation
  const createAbandonedOrder = async (phoneInput) => {
    try {
      // Set the flag to prevent duplicate orders
      abandonedOrderCreatedRef.current = true;
      console.log("Creating abandoned order from phone entry...");

      // Calculate prices - Apply promo discount to TTC then recalculate
      const productPrice = selectedPack ? selectedPack.prixPack : (hasDiscount ? (product?.prixVentePromo || 0) : (product?.prixVenteTTC || 0));
      const originalPrice = product?.prixVenteTTC || 0;
      const totalPrice = productPrice * quantity;
      const finalDeliveryFee = (product?.livraisonGratuite && quantity >= (product?.seuilLivraisonGratuite || 1)) ? 0 : deliveryFee;
      
      // Apply promo discount to TTC total
      const promoDiscount = promoCodeState.isValid ? promoCodeState.discount : 0;
      const discountedTTC = Math.max(0, totalPrice - promoDiscount);
      
      // Recalculate HT and TVA from discounted TTC
      const finalHT = discountedTTC / 1.19;
      const finalTVA = discountedTTC - finalHT;
      
      const orderTotal = discountedTTC + finalDeliveryFee;

      // Build order lines
      let orderLines = [];
      if (selectedPack && product.isVariant && packVariantSelections && packVariantSelections.length > 0) {
        // Pack with variants: one line per article in the pack
        const packRemisePercent = originalPrice > 0 ? ((originalPrice - selectedPack.prixPack) / originalPrice) * 100 : 0;
        orderLines = packVariantSelections.map((variantSelection) => {
          const lineTTC = selectedPack.prixPack;
          const lineHT = lineTTC / 1.19;
          const lineTVA = lineTTC - lineHT;
          const lineRemise = originalPrice - selectedPack.prixPack;
          return {
            productVariantId: variantSelection?.id || null,
            productId: product?.id || null,
            quantite: 1,
            prixUnitaire: Number(originalPrice.toFixed(2)),
            totalTTC: Number(lineTTC.toFixed(2)),
            totalHt: Number(lineHT.toFixed(2)),
            totalTva: Number(lineTVA.toFixed(2)),
            totalRemise: Number(lineRemise.toFixed(2)),
            tauxTva: 19,
            tauxRemise: Number(packRemisePercent.toFixed(2))
          };
        });
      } else if (selectedPack && !product.isVariant) {
        // Pack without variants: one line per article
        const packRemisePercent = originalPrice > 0 ? ((originalPrice - selectedPack.prixPack) / originalPrice) * 100 : 0;
        for (let i = 0; i < selectedPack.quantity; i++) {
          const lineTTC = selectedPack.prixPack;
          const lineHT = lineTTC / 1.19;
          const lineTVA = lineTTC - lineHT;
          const lineRemise = originalPrice - selectedPack.prixPack;
          orderLines.push({
            productVariantId: null,
            productId: product?.id || null,
            quantite: 1,
            prixUnitaire: Number(originalPrice.toFixed(2)),
            totalTTC: Number(lineTTC.toFixed(2)),
            totalHt: Number(lineHT.toFixed(2)),
            totalTva: Number(lineTVA.toFixed(2)),
            totalRemise: Number(lineRemise.toFixed(2)),
            tauxTva: 19,
            tauxRemise: Number(packRemisePercent.toFixed(2))
          });
        }
      } else {
        // Normal single product (no pack)
        orderLines = [
          {
            productVariantId: selectedVariant?.id || null,
            productId: product?.id || null,
            quantite: quantity,
            prixUnitaire: Number(productPrice.toFixed(2)),
            totalTTC: Number(discountedTTC.toFixed(2)),
            totalHt: Number(finalHT.toFixed(2)),
            totalTva: Number(finalTVA.toFixed(2)),
            totalRemise: Number(promoDiscount.toFixed(2)),
            tauxTva: 19,
            tauxRemise: 0
          }
        ];
      }

      // Prepare abandoned order data
      const abandonedOrderData = {
        entet: {
          dateBonCommande: new Date().toISOString(),
          reference: referenceRef.current,
          utilisateurId: null,
          totalTTC: Number(orderTotal.toFixed(2)),
          totalHt: Number(finalHT.toFixed(2)),
          totalTva: Number(finalTVA.toFixed(2)),
          totalRemise: Number(promoDiscount.toFixed(2)),
          codePromo: promoCodeState.isValid ? formData.codePromo : null,
          promoDiscount: Number(promoDiscount.toFixed(2)),
          source: "web_store",
          etat: "abandoned",
          fraisLivraison: Number(finalDeliveryFee.toFixed(2)),
          plateformId: platformid == 0 ? null : platformid,
          clientFirstName: "",
          clientLastName: "",
          clientNumber: phoneInput,
          clientAdresse: "",
          clientEmail: "",
          ville: ""
        },
        line: orderLines
      };

      // Use the createOrder API call
      const res = await createOrder(abandonedOrderData);
      setOrderId(res.id);
      console.log("Abandoned order created successfully");

      // Track InitiateCheckout + Lead (fire-and-forget, skip if no pixel)
      if (product.categorie?.pixelId) {
        // Persist all known identity fields so future events from this browser
        // carry hashed ph/fn/ln/ct/em (improves customer-data match coverage).
        try {
          facebookConversionService.setCustomer({
            phone: phoneInput,
            firstName: formData.firstName || undefined,
            lastName: formData.lastName || undefined,
            city: formData.city || undefined,
            email: formData.email || undefined,
            country: 'TN',
          });
        } catch { /* ignore */ }
        facebookConversionService.trackInitiateCheckout(
          { phone: phoneInput, value: orderTotal, currency: 'TND', productId: product.id },
          product.categorie
        ).catch(() => {});
        facebookConversionService.trackLead(
          { phone: phoneInput, value: orderTotal, currency: 'TND', productId: product.id },
          product.categorie
        ).catch(() => {});
      }
    } catch (error) {
      console.error("Failed to create abandoned order:", error);
    }
  };

  // NEW - Promo code validation function
  const validatePromoCode = async (code) => {
    if (!code || code.trim() === '') {
      setPromoCodeState({
        isValidating: false,
        isValid: false,
        discount: 0,
        validatedCode: null,
        error: null
      });
      return;
    }

    setPromoCodeState(prev => ({
      ...prev,
      isValidating: true,
      error: null
    }));

    try {
      const result = await promoCodeValidationService.checkPromoCode(code.trim());
      
      if (result.success) {
        setPromoCodeState({
          isValidating: false,
          isValid: true,
          discount: result.data.montant || 0,
          validatedCode: {montant : result.data, code: code.trim()},
          error: null
        });
      } else {
        setPromoCodeState({
          isValidating: false,
          isValid: false,
          discount: 0,
          validatedCode: null,
          error: result.error
        });
      }
    } catch (error) {
      setPromoCodeState({
        isValidating: false,
        isValid: false,
        discount: 0,
        validatedCode: null,
        error: 'Erreur lors de la validation du code promo'
      });
    }
  };

  // NEW - Handle promo code blur
  const handlePromoCodeBlur = () => {
    const promoCode = formData.codePromo;
    if (promoCode && promoCode.trim() !== '') {
      validatePromoCode(promoCode);
    }
  };

  // Scroll to the first invalid field (phone has priority)
  const scrollToFirstError = (newErrors) => {
    // Priority order: phone first, then other fields in form order
    const fieldPriority = ['phone', 'firstName', 'lastName', 'address', 'city'];
    const refMap = {
      phone: phoneInputRef,
      firstName: firstNameRef,
      lastName: lastNameRef,
      address: addressRef,
      city: cityRef,
    };

    for (const field of fieldPriority) {
      if (newErrors[field] && refMap[field]?.current) {
        refMap[field].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus the input after scroll
        setTimeout(() => {
          refMap[field].current?.focus?.();
        }, 400);
        return;
      }
    }
  };

  // Validation function
  const validate = () => {
    const newErrors = {};

    // Required fields
    if (!formData.firstName) newErrors.firstName = 'Le prénom est requis';
    if (!formData.lastName) newErrors.lastName = 'Le nom est requis';
    if (!formData.phone) newErrors.phone = 'Le numéro de téléphone est requis';
    if (!formData.address) newErrors.address = 'L\'adresse est requise';
    if (!formData.city) newErrors.city = 'La ville est requise';

    // Phone validation (simple Tunisian phone number check)
    if (formData.phone && !/^[2-9]\d{7}$/.test(formData.phone)) {
      newErrors.phone = 'Numéro de téléphone invalide (doit contenir 8 chiffres et commencer par 2-9)';
    }

    // Email validation (optional but must be valid if provided)
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    // NEW - Promo code validation
    if (formData.codePromo && formData.codePromo.trim() !== '' && !promoCodeState.isValid) {
      newErrors.codePromo = promoCodeState.error || 'Code promo invalide';
    }

    // Terms agreement
    if (!formData.terms) {
      newErrors.terms = 'Vous devez accepter les conditions générales';
    }

    setErrors(newErrors);

    // Scroll to the first error field
    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError(newErrors);
    }

    return Object.keys(newErrors).length === 0;
  };

  // Handle order submission
  const handleOrderSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setOrderError(null);

      // ADDED: Check promo code availability before submission
      if (promoCodeState.isValid && promoCodeState.validatedCode) {
        try {
          // Use checkout API to verify promo code is still available
          const checkoutResult = await promoCodeValidationService.checkPromoCode(promoCodeState.validatedCode.code);
          
          if (!checkoutResult.success) {
            // Promo code is no longer available
            setOrderError(`Code promo non disponible: ${checkoutResult.error}`);
            
            // Reset promo code state
            setPromoCodeState({
              isValidating: false,
              isValid: false,
              discount: 0,
              validatedCode: null,
              error: checkoutResult.error
            });
            
            // Clear promo code from form
            setFormData(prev => ({
              ...prev,
              codePromo: ''
            }));
            
            return; // Stop submission
          }
        } catch (error) {
          console.error('Error checking promo code availability:', error);
          setOrderError('Erreur lors de la vérification du code promo. Veuillez réessayer.');
          return; // Stop submission
        }
      }

      // Calculate prices - Apply promo discount to TTC then recalculate
      const productPrice = selectedPack ? selectedPack.prixPack : (hasDiscount ? (product?.prixVentePromo || 0) : (product?.prixVenteTTC || 0));
      const originalPrice = product?.prixVenteTTC || 0;
      const totalPrice = productPrice * quantity;
      const isFreeShipping = product?.livraisonGratuite && quantity >= (product?.seuilLivraisonGratuite || 1);
      const finalDeliveryFee = isFreeShipping ? 0 : deliveryFee;
      
      // Apply promo discount to TTC total
      const promoDiscount = promoCodeState.isValid ? promoCodeState.discount : 0;
      const discountedTTC = Math.max(0, totalPrice - promoDiscount);
      
      // Recalculate HT and TVA from discounted TTC
      const finalHT = discountedTTC / 1.19;
      const finalTVA = discountedTTC - finalHT;
      
      const orderTotal = discountedTTC + finalDeliveryFee;

      // Build order lines for the patch
      let orderLines = [];
      if (selectedPack && product.isVariant && packVariantSelections && packVariantSelections.length > 0) {
        const packRemisePercent = originalPrice > 0 ? ((originalPrice - selectedPack.prixPack) / originalPrice) * 100 : 0;
        orderLines = packVariantSelections.map((variantSelection) => {
          const lineTTC = selectedPack.prixPack;
          const lineHT = lineTTC / 1.19;
          const lineTVA = lineTTC - lineHT;
          const lineRemise = originalPrice - selectedPack.prixPack;
          return {
            productVariantId: variantSelection?.id || null,
            productId: product?.id || null,
            quantite: 1,
            prixUnitaire: Number(originalPrice.toFixed(2)),
            totalTTC: Number(lineTTC.toFixed(2)),
            totalHt: Number(lineHT.toFixed(2)),
            totalTva: Number(lineTVA.toFixed(2)),
            totalRemise: Number(lineRemise.toFixed(2)),
            tauxTva: 19,
            tauxRemise: Number(packRemisePercent.toFixed(2))
          };
        });
      } else if (selectedPack && !product.isVariant) {
        const packRemisePercent = originalPrice > 0 ? ((originalPrice - selectedPack.prixPack) / originalPrice) * 100 : 0;
        for (let i = 0; i < selectedPack.quantity; i++) {
          const lineTTC = selectedPack.prixPack;
          const lineHT = lineTTC / 1.19;
          const lineTVA = lineTTC - lineHT;
          const lineRemise = originalPrice - selectedPack.prixPack;
          orderLines.push({
            productVariantId: null,
            productId: product?.id || null,
            quantite: 1,
            prixUnitaire: Number(originalPrice.toFixed(2)),
            totalTTC: Number(lineTTC.toFixed(2)),
            totalHt: Number(lineHT.toFixed(2)),
            totalTva: Number(lineTVA.toFixed(2)),
            totalRemise: Number(lineRemise.toFixed(2)),
            tauxTva: 19,
            tauxRemise: Number(packRemisePercent.toFixed(2))
          });
        }
      } else {
        // No pack: still send the line explicitly. Leaving it null makes the backend
        // fall back to a flat line that carries no unit price.
        orderLines = [
          {
            productVariantId: selectedVariant?.id || null,
            productId: product?.id || null,
            quantite: quantity,
            prixUnitaire: Number(productPrice.toFixed(2)),
            totalTTC: Number(discountedTTC.toFixed(2)),
            totalHt: Number(finalHT.toFixed(2)),
            totalTva: Number(finalTVA.toFixed(2)),
            totalRemise: Number(promoDiscount.toFixed(2)),
            tauxTva: 19,
            tauxRemise: 0
          }
        ];
      }

      // If we have an abandoned order ID, update it; otherwise create a fresh order
      if (orderId) {
        const updatedOrderData = {
          id: orderId,
          clientFirstName: formData.firstName,
          clientLastName: formData.lastName,
          clientEmail: formData.email || "",
          clientAdresse: formData.address,
          clientNumber: formData.phone,
          ville: formData.city,
          etat: "pending",
          fraisDeLivraison: Number(finalDeliveryFee.toFixed(2)),
          productVariantId: selectedVariant?.id || null,
          productId: product?.id || null,
          quantite: quantity,
          totalTTC: Number(orderTotal.toFixed(2)),
          tauxTva: 19,
          totalHt: Number(finalHT.toFixed(2)),
          totalTva: Number(finalTVA.toFixed(2)),
          totalRemise: Number(promoDiscount.toFixed(2)),
          tauxRemise: 0,
          codePromo: promoCodeState.isValid ? formData.codePromo : null,
          promoDiscount: Number(promoDiscount.toFixed(2)),
          plateformId: platformid || 0,
          lines: orderLines.length > 0 ? orderLines : null
        };

        // Increment promo code usage if valid (this happens after checkout validation)
        if (promoCodeState.isValid && promoCodeState.validatedCode) {
          await promoCodeValidationService.incrementPromoCodeUsage(promoCodeState.validatedCode);
        }

        // Update the existing order
        const response = await updateOrderProduct(updatedOrderData);

        // Send confirmation email (fire-and-forget — never block, never display errors)
        if (formData.email && formData.email.trim()) {
          sendOrderConfirmationEmail(orderId);
        }

        // Track Facebook Purchase conversion (fire-and-forget — never block order)
        if (product.categorie?.pixelId) {
          facebookConversionService.trackPurchase(
            {
              orderId: orderId,
              value: orderTotal,
              currency: 'TND',
              items: [{
                productId: product.id,
                id: product.id,
                quantity: quantity,
                price: productPrice,
              }],
              customer: {
                email: formData.email,
                phone: formData.phone,
                firstName: formData.firstName,
                lastName: formData.lastName,
                city: formData.city,
                country: 'TN',
              },
            },
            product.categorie
          ).catch(err => console.error('Facebook tracking failed:', err));
        }
        
        // Handle successful order update
        setOrderSuccess(true);
        successfulSubmissionRef.current = true;

        // Reset form after successful submission
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          codePromo: '',
          notes: '',
          paymentMethod: 'cash',
          terms: true
        });

        // Reset promo code state
        setPromoCodeState({
          isValidating: false,
          isValid: false,
          discount: 0,
          validatedCode: null,
          error: null
        });

        // Redirect to thank you page with order details
        navigate(`/order-confirmation/${orderId}`, {
          state: {
            orderDetails: response,
            orderedProduct: {
              id: product.id,
              categorieId: product.categorieId || product.categorie?.id || null
            }
          }
        });
      } else {
        // No abandoned order yet — create a fresh order directly
        await fetchOrderRef();
        if (!referenceRef.current) {
          referenceRef.current = 'CMD-000001';
        }

        const orderLines = [];
        if (selectedPack && product.isVariant && packVariantSelections && packVariantSelections.length > 0) {
          const packRemisePercent = originalPrice > 0 ? ((originalPrice - selectedPack.prixPack) / originalPrice) * 100 : 0;
          packVariantSelections.forEach((variantSelection) => {
            const lineTTC = selectedPack.prixPack;
            const lineHT = lineTTC / 1.19;
            const lineTVA = lineTTC - lineHT;
            const lineRemise = originalPrice - selectedPack.prixPack;
            orderLines.push({
              productVariantId: variantSelection?.id || null,
              productId: product?.id || null,
              quantite: 1,
              prixUnitaire: Number(originalPrice.toFixed(2)),
              totalTTC: Number(lineTTC.toFixed(2)),
              totalHt: Number(lineHT.toFixed(2)),
              totalTva: Number(lineTVA.toFixed(2)),
              totalRemise: Number(lineRemise.toFixed(2)),
              tauxTva: 19,
              tauxRemise: Number(packRemisePercent.toFixed(2))
            });
          });
        } else if (selectedPack && !product.isVariant) {
          const packRemisePercent = originalPrice > 0 ? ((originalPrice - selectedPack.prixPack) / originalPrice) * 100 : 0;
          for (let i = 0; i < selectedPack.quantity; i++) {
            const lineTTC = selectedPack.prixPack;
            const lineHT = lineTTC / 1.19;
            const lineTVA = lineTTC - lineHT;
            const lineRemise = originalPrice - selectedPack.prixPack;
            orderLines.push({
              productVariantId: null,
              productId: product?.id || null,
              quantite: 1,
              prixUnitaire: Number(originalPrice.toFixed(2)),
              totalTTC: Number(lineTTC.toFixed(2)),
              totalHt: Number(lineHT.toFixed(2)),
              totalTva: Number(lineTVA.toFixed(2)),
              totalRemise: Number(lineRemise.toFixed(2)),
              tauxTva: 19,
              tauxRemise: Number(packRemisePercent.toFixed(2))
            });
          }
        } else {
          orderLines.push({
            productVariantId: selectedVariant?.id || null,
            productId: product?.id || null,
            quantite: quantity,
            prixUnitaire: Number(productPrice.toFixed(2)),
            totalTTC: Number(discountedTTC.toFixed(2)),
            totalHt: Number(finalHT.toFixed(2)),
            totalTva: Number(finalTVA.toFixed(2)),
            totalRemise: Number(promoDiscount.toFixed(2)),
            tauxTva: 19,
            tauxRemise: 0
          });
        }

        const newOrderData = {
          entet: {
            dateBonCommande: new Date().toISOString(),
            reference: referenceRef.current,
            utilisateurId: null,
            totalTTC: Number(orderTotal.toFixed(2)),
            totalHt: Number(finalHT.toFixed(2)),
            totalTva: Number(finalTVA.toFixed(2)),
            totalRemise: Number(promoDiscount.toFixed(2)),
            codePromo: promoCodeState.isValid ? formData.codePromo : null,
            promoDiscount: Number(promoDiscount.toFixed(2)),
            source: "web_store",
            etat: "pending",
            fraisLivraison: Number(finalDeliveryFee.toFixed(2)),
            plateformId: platformid == 0 ? null : platformid,
            clientFirstName: formData.firstName,
            clientLastName: formData.lastName,
            clientNumber: formData.phone,
            clientAdresse: formData.address,
            clientEmail: formData.email || "",
            ville: formData.city
          },
          line: orderLines
        };

        if (promoCodeState.isValid && promoCodeState.validatedCode) {
          await promoCodeValidationService.incrementPromoCodeUsage(promoCodeState.validatedCode);
        }

        const res = await createOrder(newOrderData);

        // Send confirmation email (fire-and-forget — never block, never display errors)
        if (formData.email && formData.email.trim() && res?.id) {
          sendOrderConfirmationEmail(res.id);
        }

        // Track Facebook Purchase (fire-and-forget — never block order)
        if (product.categorie?.pixelId) {
          facebookConversionService.trackPurchase(
            {
              orderId: res.id,
              value: orderTotal,
              currency: 'TND',
              items: [{
                productId: product.id,
                id: product.id,
                quantity: quantity,
                price: productPrice,
              }],
              customer: {
                email: formData.email,
                phone: formData.phone,
                firstName: formData.firstName,
                lastName: formData.lastName,
                city: formData.city,
                country: 'TN',
              },
            },
            product.categorie
          ).catch(err => console.error('Facebook tracking failed:', err));
        }

        setOrderSuccess(true);
        successfulSubmissionRef.current = true;
        setFormData({
          firstName: '', lastName: '', email: '', phone: '', address: '', city: '',
          codePromo: '', notes: '', paymentMethod: 'cash', terms: true
        });
        setPromoCodeState({ isValidating: false, isValid: false, discount: 0, validatedCode: null, error: null });
        navigate(`/order-confirmation/${res.id}`, {
          state: {
            orderDetails: res,
            orderedProduct: {
              id: product.id,
              categorieId: product.categorieId || product.categorie?.id || null
            }
          }
        });
      }
    } catch (error) {
      console.error('Error updating order:', error);
      setOrderError('Une erreur est survenue lors de la mise à jour de votre commande. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render variant options - UPDATED to handle both single and multi-type variants
const renderVariantOptions = () => {
  if (!product || !product.productVariants || product.productVariants.length === 0) {
    return null;
  }

  // Check if variants follow the "Color - Size" pattern or are single-type
  const hasMultiTypeVariants = product.productVariants.some(variant => 
    variant.name.includes(' - ')
  );

  if (hasMultiTypeVariants) {
    // Handle multi-type variants (Color - Size format)
    const uniqueValues = {};
    
    product.productVariants.forEach(variant => {
      const parts = variant.name.split(' - ');
      if (parts.length === 2) {
        const [color, size] = parts;
        if (!uniqueValues['color']) uniqueValues['color'] = new Set();
        if (!uniqueValues['size']) uniqueValues['size'] = new Set();
        uniqueValues['color'].add(color);
        uniqueValues['size'].add(size);
      }
    });

    const sortVariantValues = (arr) => sortByLabelAsc(arr);

    const colors = sortVariantValues(Array.from(uniqueValues['color'] || []));
    const sizes = sortVariantValues(Array.from(uniqueValues['size'] || []));

    // Helper to check if a combination exists and is in stock
    const getCombinationStatus = (color, size) => {
      const variant = product.productVariants.find(v =>
        v.name === `${color} - ${size}`
      );
      if (!variant) return 'unavailable';
      if (product.isStockRequired && variant.stock <= 0) return 'outOfStock';
      return 'available';
    };

    const currentSelection = selectedVariant ? selectedVariant.name.split(' - ') : ['', ''];
    const [selectedColor, selectedSize] = currentSelection;

    return (
      <div className="space-y-5">
        {/* Color selection */}
        {colors.length > 0 && (
          <div>
            <h3 className="text-sm sm:text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">{variantTypes && variantTypes[0] ? variantTypes[0] : 'Couleur'}</h3>
            <div className="flex flex-wrap gap-2">
              {colors.map(color => {
                const hasAvailableVariant = sizes.some(size =>
                  getCombinationStatus(color, size) === 'available'
                );

                return (
                  <button
                    key={color}
                    type="button"
                    onClick={() => {
                      const validSize = sizes.find(size =>
                        getCombinationStatus(color, size) === 'available'
                      ) || sizes[0];

                      const targetVariant = product.productVariants.find(v =>
                        v.name === `${color} - ${validSize}`
                      );

                      if (targetVariant) {
                        onVariantChange(targetVariant.id);
                      }
                    }}
                    className={`px-5 py-2.5 text-base sm:text-sm font-bold border-2 rounded-full transition-all duration-200 ${color === selectedColor
                        ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                        : hasAvailableVariant
                          ? 'border-gray-300 bg-white text-gray-800 hover:border-gray-900 hover:shadow-sm'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    disabled={!hasAvailableVariant}
                  >
                    {color}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Size selection */}
        {sizes.length > 0 && (
          <div>
            <h3 className="text-sm sm:text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">{variantTypes && variantTypes[1] ? variantTypes[1] : 'Taille'}</h3>
            <div className="flex flex-wrap gap-2">
              {sizes.map(size => {
                const status = getCombinationStatus(selectedColor, size);
                const isAvailable = status === 'available';
                const isSelected = size === selectedSize;

                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() => {
                      if (!isAvailable) return;

                      const targetVariant = product.productVariants.find(v =>
                        v.name === `${selectedColor} - ${size}`
                      );

                      if (targetVariant) {
                        onVariantChange(targetVariant.id);
                      }
                    }}
                    className={`px-5 py-2.5 text-base sm:text-sm font-bold border-2 rounded-full transition-all duration-200 ${isSelected
                        ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                        : isAvailable
                          ? 'border-gray-300 bg-white text-gray-800 hover:border-gray-900 hover:shadow-sm'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    disabled={!isAvailable}
                  >
                    {size}
                    {status === 'outOfStock' && <span className="ml-1 text-xs font-normal">(Épuisé)</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  } else {
    // Handle single-type variants (like BMW, KIAx, VW)
    const sortedVariants = sortByLabelAsc(product.productVariants, (v) => v.name);

    return (
      <div className="space-y-5">
        <div>
          <h3 className="text-sm sm:text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">{variantTypes && variantTypes[0] ? variantTypes[0] : 'Modèle'}</h3>
          <div className="flex flex-wrap gap-2">
            {sortedVariants.map(variant => {
              const isSelected = selectedVariant && selectedVariant.id === variant.id;
              const isAvailable = product.isStockRequired ? variant.stock > 0 : true;

              return (
                <button
                  key={variant.id}
                  type="button"
                  onClick={() => {
                    if (isAvailable) {
                      onVariantChange(variant.id);
                    }
                  }}
                  className={`px-5 py-2.5 text-base sm:text-sm font-bold border-2 rounded-full transition-all duration-200 ${isSelected
                      ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                      : isAvailable
                        ? 'border-gray-300 bg-white text-gray-800 hover:border-gray-900 hover:shadow-sm'
                        : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  disabled={!isAvailable}
                >
                  {variant.name}
                  {!isAvailable && <span className="ml-1 text-xs font-normal">(Épuisé)</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
};

  if (!product) return null;

  return (
    <div className="space-y-6">
      {/* Product name and stock info - Hidden on mobile */}
      <div className="hidden md:block">
        <h1 className={`text-2xl font-bold sm:text-3xl ${tStyles.titleColor}`}>{product.name}</h1>

        {product.reference && (
          <p className={`mt-1 text-sm ${tStyles.mutedText}`}>
            Réf: {selectedVariant ? selectedVariant.reference : product.reference}
          </p>
        )}
      </div>

      {/* Pricing */}
      <div className="flex items-baseline flex-wrap gap-x-2.5 gap-y-1">
        {selectedPack ? (
          <>
            <p className={`text-3xl sm:text-2xl font-bold tracking-tight ${tStyles.priceColor}`}>{selectedPack.prixPack?.toFixed(2)} TND <span className="text-base sm:text-sm font-normal text-gray-500">/ pièce</span></p>
            <p className={`text-lg sm:text-base line-through ${tStyles.mutedText}`}>{product.prixVenteTTC?.toFixed(2)} TND</p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm sm:text-xs font-medium bg-green-100 text-green-800">
              Pack de {selectedPack.quantity}
            </span>
          </>
        ) : hasDiscount ? (
          <>
            <p className="text-3xl sm:text-2xl font-bold tracking-tight text-red-600">{product.prixVentePromo?.toFixed(2)} TND</p>
            <p className="text-lg sm:text-base line-through text-gray-400">{product.prixVenteTTC?.toFixed(2)} TND</p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm sm:text-xs font-semibold bg-green-100 text-green-700">
              -{discountPercentage}%
            </span>
          </>
        ) : (
          <p className={`text-3xl sm:text-2xl font-bold tracking-tight ${tStyles.priceColor}`}>{product.prixVenteTTC?.toFixed(2)} TND</p>
        )}
      </div>

      {product.isStockRequired && <div className="flex items-center">
        <div className={`flex-shrink-0 h-3 w-3 rounded-full ${
          // For products without variants, use product.stock
          (!product.isVariant || !product.productVariants || product.productVariants.length === 0)
            ? (product.stock > 0 
                ? (product.stock <= 5 ? 'bg-amber-500' : 'bg-blue-500')
                : 'bg-red-500')
            : (selectedVariant && selectedVariant.stock > 0)
                ? (selectedVariant.stock <= 5 ? 'bg-amber-500' : 'bg-blue-500')
                : 'bg-red-500'
          } mr-2`}></div>
        <p className={`text-base sm:text-sm font-medium ${stockLabelColor()}`}>
          {getStockLabel()}
        </p>
      </div>}

      {/* Product Description - Hidden on mobile */}
      {product.description && product.description.trim() && (
        <div className={`hidden md:block mt-4 border-t border-b py-4 ${tStyles.dividerColor}`}>
          <h3 className={`text-sm font-medium mb-2 ${tStyles.sectionTitle}`}>Description</h3>
          <div className={`prose prose-sm max-w-none ${tStyles.descText}`}>
            <p
              style={!showFullDesc ? {
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              } : undefined}
            >
              {product.description}
            </p>
            <button
              type="button"
              onClick={() => setShowFullDesc(prev => !prev)}
              className={`mt-1 text-sm font-medium underline ${tStyles.mutedText}`}
            >
              {showFullDesc ? 'Voir moins' : 'Voir plus'}
            </button>
          </div>
        </div>
      )}

      {/* Variant Selection - hidden when pack is selected */}
      {!selectedPack && renderVariantOptions()}

      {/* Pack Options — gold frame with a pulsing halo, so the offers catch the eye */}
      {product.hasPack && product.packProducts && product.packProducts.length > 0 && (
        <div className="relative mt-4 rounded-2xl border-2 border-gold bg-gradient-to-b from-[#FFFBEA] to-white p-4 animate-gold-glow motion-reduce:animate-none">
          <span className="absolute -top-3 left-4 rounded-full bg-gradient-to-r from-gold-dark via-gold to-gold-light bg-[length:200%_auto] px-3 py-0.5 text-[11px] font-extrabold uppercase tracking-wide text-white shadow-md animate-gold-shine motion-reduce:animate-none">
            Offre spéciale
          </span>
          <h3 className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gold-dark">
            <svg className="h-4 w-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a1 1 0 01.894.553l1.382 2.764 3.05.443a1 1 0 01.555 1.705l-2.207 2.152.521 3.038a1 1 0 01-1.451 1.054L10 12.347l-2.744 1.362a1 1 0 01-1.45-1.054l.52-3.038-2.207-2.152a1 1 0 01.555-1.705l3.05-.443L9.106 2.553A1 1 0 0110 2z" />
            </svg>
            Offres Pack
          </h3>
          <div className="space-y-4">
            {product.packProducts.map((pack, idx) => {
              const isSelected = selectedPack && selectedPack.quantity === pack.quantity;
              const totalPackPrice = pack.prixPack * pack.quantity;
              const totalNormalPrice = product.prixVenteTTC * pack.quantity;
              const totalSavings = totalNormalPrice - totalPackPrice;
              const savingsPercent = totalNormalPrice > 0 ? Math.round((totalSavings / totalNormalPrice) * 100) : 0;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => onPackSelect(pack)}
                  className={`relative w-full rounded-xl border-2 transition-all duration-200 text-left bg-white ${
                    isSelected
                      ? 'border-gray-900 ring-1 ring-gray-900 shadow-md'
                      : 'border-gray-200 hover:border-gray-400 hover:shadow-sm'
                  }`}
                >
                  {/* Floating discount badge */}
                  {savingsPercent > 0 && (
                    <span className="absolute -top-3 right-4 bg-green-500 text-white text-[11px] font-extrabold px-3 py-1 rounded-full shadow-md tracking-wide">
                      -{savingsPercent}%
                    </span>
                  )}
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? 'border-gray-900' : 'border-gray-300'
                      }`}>
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-gray-900"></div>}
                      </div>
                      <div className="min-w-0">
                        <span className="block text-sm font-bold text-gray-900 truncate">
                          Achetez {pack.quantity} {pack.quantity > 1 ? 'pièces' : 'pièce'}
                        </span>
                        {totalSavings > 0 && (
                          <span className="block text-xs font-semibold text-green-600 mt-0.5">
                            Économisez {totalSavings.toFixed(0)} DT
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3 flex items-baseline gap-2">
                      {totalSavings > 0 && (
                        <span className="text-sm text-gray-400 line-through font-medium">{totalNormalPrice.toFixed(0)} DT</span>
                      )}
                      <span className="text-xl font-extrabold text-gray-900">{totalPackPrice.toFixed(0)} DT</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Pack variant selections - select variant for each item in pack */}
          {selectedPack && product.isVariant && product.productVariants && product.productVariants.length > 0 && (
            <div className="mt-4 space-y-4">
              <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                Choisissez une variante pour chaque article ({selectedPack.quantity} articles)
              </h4>
              {packVariantSelections.map((selection, slotIdx) => {
                // Determine if multi-type (Color - Size) or single-type
                const hasMultiType = product.productVariants.some(v => v.name.includes(' - '));
                const slotSelection = selection ? selection.name : '';
                const slotParts = slotSelection ? slotSelection.split(' - ') : ['', ''];

                if (hasMultiType) {
                  const uniqueVals = {};
                  product.productVariants.forEach(v => {
                    const parts = v.name.split(' - ');
                    if (parts.length === 2) {
                      if (!uniqueVals['color']) uniqueVals['color'] = new Set();
                      if (!uniqueVals['size']) uniqueVals['size'] = new Set();
                      uniqueVals['color'].add(parts[0]);
                      uniqueVals['size'].add(parts[1]);
                    }
                  });
                  const colors = Array.from(uniqueVals['color'] || []);
                  const sizes = Array.from(uniqueVals['size'] || []);
                  const [slotColor, slotSize] = slotParts;

                  const getStatus = (color, size) => {
                    const v = product.productVariants.find(x => x.name === `${color} - ${size}`);
                    if (!v) return 'unavailable';
                    if (product.isStockRequired && v.stock <= 0) return 'outOfStock';
                    return 'available';
                  };

                  return (
                    <div key={slotIdx} className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                      <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Article {slotIdx + 1}</p>
                      {/* Color row */}
                      <div className="mb-3">
                        <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">{variantTypes && variantTypes[0] ? variantTypes[0] : 'Couleur'}</span>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {colors.map(color => {
                            const hasAvailable = sizes.some(s => getStatus(color, s) === 'available');
                            const isColorSelected = color === slotColor;
                            return (
                              <button
                                key={color}
                                type="button"
                                onClick={() => {
                                  if (!hasAvailable) return;
                                  const validSize = sizes.find(s => getStatus(color, s) === 'available') || sizes[0];
                                  const target = product.productVariants.find(v => v.name === `${color} - ${validSize}`);
                                  if (target) onPackVariantSelect(slotIdx, target.id);
                                }}
                                className={`px-5 py-2.5 text-base sm:text-sm font-bold border-2 rounded-full transition-all duration-200 ${
                                  isColorSelected
                                    ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                                    : hasAvailable
                                      ? 'border-gray-300 bg-white text-gray-800 hover:border-gray-900 hover:shadow-sm'
                                      : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                                disabled={!hasAvailable}
                              >
                                {color}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {/* Size row */}
                      <div>
                        <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">{variantTypes && variantTypes[1] ? variantTypes[1] : 'Taille'}</span>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {sizes.map(size => {
                            const status = getStatus(slotColor || colors[0], size);
                            const isAvailable = status === 'available';
                            const isSizeSelected = size === slotSize;
                            return (
                              <button
                                key={size}
                                type="button"
                                onClick={() => {
                                  if (!isAvailable) return;
                                  const c = slotColor || colors[0];
                                  const target = product.productVariants.find(v => v.name === `${c} - ${size}`);
                                  if (target) onPackVariantSelect(slotIdx, target.id);
                                }}
                                className={`px-5 py-2.5 text-base sm:text-sm font-bold border-2 rounded-full transition-all duration-200 ${
                                  isSizeSelected
                                    ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                                    : isAvailable
                                      ? 'border-gray-300 bg-white text-gray-800 hover:border-gray-900 hover:shadow-sm'
                                      : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                                disabled={!isAvailable}
                              >
                                {size}
                                {status === 'outOfStock' && <span className="ml-1 text-xs font-normal">(Épuisé)</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  // Single-type variants
                  return (
                    <div key={slotIdx} className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                      <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Article {slotIdx + 1}</p>
                      <div>
                        <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">{variantTypes && variantTypes[0] ? variantTypes[0] : 'Modèle'}</span>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {product.productVariants.map(variant => {
                            const isSlotSelected = selection && selection.id === variant.id;
                            const isAvailable = product.isStockRequired ? variant.stock > 0 : true;
                            return (
                              <button
                                key={variant.id}
                                type="button"
                                onClick={() => isAvailable && onPackVariantSelect(slotIdx, variant.id)}
                                className={`px-5 py-2.5 text-base sm:text-sm font-bold border-2 rounded-full transition-all duration-200 ${
                                  isSlotSelected
                                    ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                                    : isAvailable
                                      ? 'border-gray-300 bg-white text-gray-800 hover:border-gray-900 hover:shadow-sm'
                                      : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                                disabled={!isAvailable}
                              >
                                {variant.name}
                                {!isAvailable && <span className="ml-1 text-xs font-normal">(Épuisé)</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          )}
        </div>
      )}

      {/* Quantity selector - hidden when pack is selected */}
      {!selectedPack && (
      <div className="flex items-center">
        <span className="mr-3 text-sm font-bold text-gray-900">Quantité</span>
        <div className={`flex items-center border rounded-md ${tStyles.quantityBorder}`}>
          <button
            type="button"
            className={`p-2 focus:outline-none ${tStyles.quantityBtn}`}
            onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
            disabled={quantity <= 1}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
            </svg>
          </button>
          <span className={`w-10 text-center ${tStyles.quantityText}`}>{quantity}</span>
          <button
            type="button"
            className={`p-2 focus:outline-none ${tStyles.quantityBtn}`}
            onClick={() => {
              const maxStock = product.isVariant 
                ? (selectedVariant ? selectedVariant.stock : 0)
                : product.stock;
              onQuantityChange(product.isStockRequired ? Math.min(maxStock, quantity + 1) : quantity + 1);
            }}
            disabled={
              product.isStockRequired
                ? (product.isVariant 
                    ? (!selectedVariant || quantity >= selectedVariant.stock)
                    : (quantity >= product.stock))
                : false
            }
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>
      )}

      {/* Delivery info banner */}
      <div className={`mt-6 p-4 border rounded-lg ${tStyles.deliveryBg}`}>
        <div className="flex">
          <svg className={`h-5 w-5 flex-shrink-0 ${tStyles.deliveryIcon}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <p className={`text-sm ${tStyles.deliveryText}`}>
              {isFreeDelivery
                ? <span className="font-medium">Livraison gratuite!</span>
                : product?.livraisonGratuite
                  ? <span>Livraison <span className="font-medium">{deliveryFee?.toFixed(2)} TND</span>. Gratuite dès <span className="font-medium">{product?.seuilLivraisonGratuite || 2} article{(product?.seuilLivraisonGratuite || 2) > 1 ? 's' : ''}</span> commandés.</span>
                  : <span>Livraison <span className="font-medium">{deliveryFee?.toFixed(2)} TND</span></span>
              }
            </p>
          </div>
        </div>
      </div>

      {/* Client Information Form */}
      <div className={`mt-8 border-t pt-6 ${tStyles.dividerColor}`}>
        <h3 className={`text-lg font-medium mb-4 ${tStyles.sectionTitle}`}>Informations de commande</h3>

        <form ref={formRef} onSubmit={handleOrderSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6">
            {/* First Name */}
            <div className="sm:col-span-3">
              <label htmlFor="firstName" className="block text-sm font-bold text-black mb-1">
                Prénom (الاسم) <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineUser className={`h-5 w-5 ${tStyles.iconColor}`} />
                </div>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  ref={firstNameRef}
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`pl-10 block w-full py-3 border ${errors.firstName
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : `${tStyles.inputBorder} ${tStyles.inputFocusRing}`
                    } ${tStyles.inputBg} text-black font-bold rounded-md shadow-sm ${tStyles.inputPlaceholder} sm:text-sm transition-all`}
                  placeholder="Votre prénom"
                />
              </div>
              {errors.firstName && (
                <p className="mt-2 text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div className="sm:col-span-3">
              <label htmlFor="lastName" className="block text-sm font-bold text-black mb-1">
                Nom (اللقب) <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineUser className={`h-5 w-5 ${tStyles.iconColor}`} />
                </div>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  ref={lastNameRef}
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`pl-10 block w-full py-3 border ${errors.lastName
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : `${tStyles.inputBorder} ${tStyles.inputFocusRing}`
                    } ${tStyles.inputBg} text-black font-bold rounded-md shadow-sm ${tStyles.inputPlaceholder} sm:text-sm transition-all`}
                  placeholder="Votre nom"
                />
              </div>
              {errors.lastName && (
                <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>
              )}
            </div>

            {/* Email */}
            <div className="sm:col-span-3">
              <label htmlFor="email" className="block text-sm font-bold text-black mb-1">
                Email (البريد الإلكتروني)
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineMail className={`h-5 w-5 ${tStyles.iconColor}`} />
                </div>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`pl-10 block w-full py-3 border ${errors.email
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : `${tStyles.inputBorder} ${tStyles.inputFocusRing}`
                    } ${tStyles.inputBg} text-black font-bold rounded-md shadow-sm ${tStyles.inputPlaceholder} sm:text-sm transition-all`}
                  placeholder="exemple@email.com"
                />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div className="sm:col-span-3">
              <label htmlFor="phone" className="block text-sm font-bold text-black mb-1">
                Téléphone (رقم الهاتف) <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none select-none gap-1.5">
                  <img
                    src="https://flagcdn.com/w40/tn.png"
                    alt="Tunisie"
                    className="h-4 w-6 rounded-sm object-cover"
                    draggable={false}
                  />
                  <span className="text-sm font-bold text-black">+216</span>
                  <span className={`h-5 border-l ${tStyles.inputBorder} ml-1`}></span>
                </div>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  ref={phoneInputRef}
                  value={formData.phone}
                  onChange={handleChange}
                  inputMode="numeric"
                  maxLength={8}
                  className={`pl-[6.5rem] block w-full py-3 border ${errors.phone
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : `${tStyles.inputBorder} ${tStyles.inputFocusRing}`
                    } ${tStyles.inputBg} text-black font-bold rounded-md shadow-sm ${tStyles.inputPlaceholder} sm:text-sm transition-all`}
                  placeholder="Votre numéro (8 chiffres)"
                />
              </div>
              {errors.phone && (
                <p className="mt-2 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            {/* Address */}
            <div className="sm:col-span-6">
              <label htmlFor="address" className="block text-sm font-bold text-black mb-1">
                Adresse (العنوان) <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineLocationMarker className={`h-5 w-5 ${tStyles.iconColor}`} />
                </div>
                <input
                  type="text"
                  name="address"
                  id="address"
                  ref={addressRef}
                  value={formData.address}
                  onChange={handleChange}
                  className={`pl-10 block w-full py-3 border ${errors.address
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : `${tStyles.inputBorder} ${tStyles.inputFocusRing}`
                    } ${tStyles.inputBg} text-black font-bold rounded-md shadow-sm ${tStyles.inputPlaceholder} sm:text-sm transition-all`}
                  placeholder="Adresse complète"
                />
              </div>
              {errors.address && (
                <p className="mt-2 text-sm text-red-600">{errors.address}</p>
              )}
            </div>

            {/* City - Replace the existing city select with this */}
<div className="sm:col-span-3">
  <label htmlFor="city" className="block text-sm font-bold text-black mb-1">
    Ville (المدينة) <span className="text-red-500">*</span>
  </label>
  <div className="mt-1 relative rounded-md shadow-sm">
    <CustomDropdown
      name="city"
      value={formData.city}
      onChange={handleChange}
      options={tunisianCities}
      placeholder="Sélectionnez une ville"
      searchPlaceholder="Rechercher une ville..."
      error={!!errors.city}
      icon={HiOutlineOfficeBuilding}
      valueKey="value"
      labelKey="label"
    />
  </div>
  {errors.city && (
    <p className="mt-2 text-sm text-red-600">{errors.city}</p>
  )}
  <div ref={cityRef} />
</div>

            {/* Postal Code */}
            <div className="sm:col-span-3">
              <label htmlFor="codePromo" className="block text-sm font-bold text-black mb-1">
                Code promo (رمز التخفيض)
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineMailOpen className={`h-5 w-5 ${tStyles.iconColor}`} />
                </div>
                <input
                  type="text"
                  name="codePromo"
                  id="codePromo"
                  value={formData.codePromo}
                  onChange={handleChange}
                  onBlur={handlePromoCodeBlur}
                  className={`pl-10 block w-full py-3 border ${
                    errors.codePromo
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : promoCodeState.isValid
                      ? 'border-green-300 text-green-900 focus:ring-green-500 focus:border-green-500'
                      : `${tStyles.inputBorder} ${tStyles.inputFocusRing}`
                  } ${tStyles.inputBg} text-black font-bold rounded-md shadow-sm ${tStyles.inputPlaceholder} sm:text-sm transition-all`}
                  placeholder="Code promo (optionnel)"
                />
                
                {/* Validation indicator */}
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {promoCodeState.isValidating && (
                    <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {promoCodeState.isValid && (
                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {promoCodeState.error && (
                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
              </div>
              
              {/* Promo code messages */}
              {errors.codePromo && (
                <p className="mt-2 text-sm text-red-600">{errors.codePromo}</p>
              )}
              {promoCodeState.isValid && (
                <p className="mt-2 text-sm text-green-600">
                  Code promo valide! Réduction de {promoCodeState.discount.toFixed(2)} TND appliquée.
                </p>
              )}
              {promoCodeState.error && !errors.codePromo && (
                <p className="mt-2 text-sm text-red-600">{promoCodeState.error}</p>
              )}
            </div>
          </div>

          {/* MOVED: Order summary - now below the client information form */}
          <div className="mt-8 p-6 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="font-medium text-gray-900 mb-4">Résumé de la commande</h4>
            <div className="flex justify-between mb-2">
              <span>Prix unitaire:</span>
              <span>
                {hasDiscount ? (
                  <>
                    <span className="line-through text-gray-400 mr-2">{product.prixVenteTTC?.toFixed(2)} TND</span>
                    <span>{product.prixVentePromo?.toFixed(2)} TND</span>
                  </>
                ) : (
                  <>{product.prixVenteTTC?.toFixed(2)} TND</>
                )}
              </span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Quantité:</span>
              <span>{quantity}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Sous-total:</span>
              <span>{productTotal.toFixed(2)} TND</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Livraison:</span>
              <span>{isFreeDelivery ? 'Gratuite' : `${finalDeliveryFee?.toFixed(2)} TND`}</span>
            </div>
            
            {/* Promo discount line */}
            {promoCodeState.isValid && promoDiscount > 0 && (
              <div className="flex justify-between mb-2 text-green-600">
                <span>Réduction (Code promo):</span>
                <span>-{promoDiscount.toFixed(2)} TND</span>
              </div>
            )}
            
            <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between font-medium">
              <span>Total:</span>
              <span>{grandTotal.toFixed(2)} TND</span>
            </div>
          </div>

          {/* Error message */}
          {orderError && (
            <div className="rounded-md bg-red-50 p-4 mt-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{orderError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success message */}
          {orderSuccess && (
            <div className="rounded-md bg-green-50 p-4 mt-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">Commande créée avec succès! Nous vous contacterons bientôt.</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit buttons */}
          <div ref={submitButtonRef} className={`mt-6 grid ${selectedPack ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
            {/* Ajouter au panier button - hidden when pack is selected */}
            {!selectedPack && (
            <button
              type="button"
              onClick={onAddToCart}
              disabled={
                product.isStockRequired && (
                  product.isVariant 
                    ? (!selectedVariant || selectedVariant.stock <= 0)
                    : (!product.stock || product.stock <= 0)
                )
              }
              className={`flex items-center justify-center px-8 py-3 border text-base font-medium rounded-md 
                ${(product.isStockRequired && (
                    product.isVariant 
                      ? (!selectedVariant || selectedVariant.stock <= 0)
                      : (!product.stock || product.stock <= 0)))
                  ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed'
                  : 'border-[#4983a9] bg-white text-[#4983a9] hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4983a9] transition-colors'
                }`}
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              Ajouter au panier
            </button>
            )}
            
            {/* Commander maintenant button */}
            <button
              type="submit"
              disabled={
                isSubmitting || 
                (product.isStockRequired && (
                  product.isVariant 
                    ? (!selectedVariant || selectedVariant.stock <= 0)
                    : (!product.stock || product.stock <= 0)))
              }
              className={`flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md 
                ${(isSubmitting || 
                   (product.isStockRequired && (
                     product.isVariant 
                       ? (!selectedVariant || selectedVariant.stock <= 0)
                       : (!product.stock || product.stock <= 0))))
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'text-white bg-[#4983a9] hover:bg-[#0f2e2a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4983a9] transition-colors'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Traitement...
                </>
              ) : 'Commander maintenant'}
            </button>
          </div>

          {/* Reassurance row */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-600">
            <span className="flex items-center gap-1">
              <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              Paiement à la livraison
            </span>
            <span className="flex items-center gap-1">
              <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              Livraison 24-48h
            </span>
            <span className="flex items-center gap-1">
              <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              Échange facile
            </span>
          </div>
        </form>
      </div>

      {/* Added to cart notification */}
      <AnimatePresence>
        {showAddedToCart && (
          <motion.div
            variants={cartNotification}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed bottom-6 right-6 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center z-50"
          >
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Produit ajouté au panier</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fixed mobile Commander button - only visible on small screens, hides when original button is in view */}
      {!orderSuccess && showFixedButton && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] px-4 py-3">
          <button
            type="button"
            onClick={() => {
              if (formRef.current) {
                formRef.current.requestSubmit();
              }
            }}
            disabled={
              isSubmitting ||
              (product.isStockRequired && (
                product.isVariant
                  ? (!selectedVariant || selectedVariant.stock <= 0)
                  : (!product.stock || product.stock <= 0)))
            }
            className={`w-full flex items-center justify-center py-3.5 rounded-lg text-base font-semibold transition-colors
              ${(isSubmitting ||
                 (product.isStockRequired && (
                   product.isVariant
                     ? (!selectedVariant || selectedVariant.stock <= 0)
                     : (!product.stock || product.stock <= 0))))
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : `${tStyles.submitText} ${tStyles.submitBg} ${tStyles.submitHover}`
            }`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Traitement...
              </>
            ) : (
              <>
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Commander maintenant
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export const MobileProductHeader = ({ product, selectedVariant }) => {
  const tStyles = useProductInfoStyles();
  // The description is shown under the price (QuickBuyProductInfo).
  return (
    <div className="block md:hidden mb-4 px-4">
      {/* Product name */}
      <div>
        <h1 className={`text-xl font-bold ${tStyles.titleColor}`}>{product.name}</h1>

        {product.reference && (
          <p className={`mt-1 text-sm ${tStyles.mutedText}`}>
            Réf: {selectedVariant ? selectedVariant.reference : product.reference}
          </p>
        )}
      </div>
    </div>
  );
};

export default ProductInfo;